import React, { useEffect } from 'react';
import classNames from 'classnames';

import Field, { hasDataInFields } from '../../Field';
import BlockBuilder from '../../BlockBuilder';

import SectionContainer from '../SectionContainer';
import css from './SectionUsersCarousel.module.css';

const KEY_CODE_ARROW_LEFT = 37;
const KEY_CODE_ARROW_RIGHT = 39;

const COLUMN_CONFIG = [
  { css: css.oneColumn, responsiveImageSizes: '(max-width: 767px) 100vw, 1200px' },
  { css: css.twoColumns, responsiveImageSizes: '(max-width: 767px) 100vw, 600px' },
  { css: css.threeColumns, responsiveImageSizes: '(max-width: 767px) 100vw, 400px' },
  { css: css.fourColumns, responsiveImageSizes: '(max-width: 767px) 100vw, 290px' },
];
const getIndex = numColumns => numColumns - 1;
const getColumnCSS = numColumns => {
  const config = COLUMN_CONFIG[getIndex(numColumns)];
  return config ? config.css : COLUMN_CONFIG[0].css;
};
const getResponsiveImageSizes = numColumns => {
  const config = COLUMN_CONFIG[getIndex(numColumns)];
  return config ? config.responsiveImageSizes : COLUMN_CONFIG[0].responsiveImageSizes;
};

// Wine 4-point sparkle (exact Figma SVG)
const SparkleIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    className={css.sparkle}
  >
    <path
      d="M24 12C17.7782 12 12 17.7782 12 24C12 17.7782 6.22183 12 0 12C6.22183 12 12 6.22183 12 0C12 6.22183 17.7782 12 24 12Z"
      fill="#8C265C"
    />
  </svg>
);

/**
 * Renders the section title parsing **bold** markers as wine-accent spans.
 */
const AccentTitle = ({ data }) => {
  if (!data?.content) return null;
  const parts = data.content.split(/\*\*(.*?)\*\*/g);
  return (
    <h2 className={css.title}>
      {parts.map((part, i) =>
        i % 2 === 1 ? (
          <span key={i} className={css.titleAccent}>{part}</span>
        ) : (
          part
        )
      )}
    </h2>
  );
};

const SectionUsersCarousel = props => {
  const {
    sectionId,
    className,
    rootClassName,
    numColumns = 3,
    title,
    description,
    appearance,
    blocks = [],
    options,
  } = props;

  const sliderContainerId = `${sectionId}-container`;
  const sliderId = `${sectionId}-slider`;
  const numberOfBlocks = blocks?.length;
  const hasBlocks = numberOfBlocks > 0;

  useEffect(() => {
    const setCarouselWidth = () => {
      if (hasBlocks) {
        const elem = window.document.getElementById(sliderContainerId);
        if (!elem) return;
        const scrollbarWidth = window.innerWidth - document.body.clientWidth;
        const carouselWidth = elem.clientWidth - scrollbarWidth;
        elem.style.setProperty('--carouselWidth', `${carouselWidth}px`);
      }
    };
    setCarouselWidth();
    window.addEventListener('resize', setCarouselWidth);
    return () => window.removeEventListener('resize', setCarouselWidth);
  }, []);

  const fieldComponents = options?.fieldComponents;
  const fieldOptions = { fieldComponents };

  const hasHeaderFields = hasDataInFields([title, description], fieldOptions);

  const blockOptions = options;

  const onSlideLeft = e => {
    const slider = window.document.getElementById(sliderId);
    if (!slider) return;
    const slideWidth = numColumns * slider.firstChild?.clientWidth;
    slider.scrollLeft -= slideWidth;
    e.target.focus();
  };

  const onSlideRight = e => {
    const slider = window.document.getElementById(sliderId);
    if (!slider) return;
    const slideWidth = numColumns * slider.firstChild?.clientWidth;
    slider.scrollLeft += slideWidth;
    e.target.focus();
  };

  const onKeyDown = e => {
    if (e.keyCode === KEY_CODE_ARROW_LEFT) {
      e.preventDefault();
      onSlideLeft(e);
    } else if (e.keyCode === KEY_CODE_ARROW_RIGHT) {
      e.preventDefault();
      onSlideRight(e);
    }
  };

  return (
    <SectionContainer
      id={sectionId}
      className={className}
      rootClassName={rootClassName || css.root}
      appearance={appearance}
      options={fieldOptions}
    >
      {hasHeaderFields ? (
        <div className={css.headerRow}>
          <div className={css.titleRow}>
            <span className={css.titleLine} />
            <SparkleIcon />
            <AccentTitle data={title} />
            <SparkleIcon />
            <span className={css.titleLine} />
          </div>
          <Field data={description} className={css.descriptionField} options={fieldOptions} />
        </div>
      ) : null}

      {hasBlocks ? (
        <div className={css.carouselContainer} id={sliderContainerId}>
          <div
            className={classNames(css.carouselArrows, {
              [css.notEnoughBlocks]: numberOfBlocks <= numColumns,
            })}
          >
            <button className={css.carouselArrowPrev} onClick={onSlideLeft} onKeyDown={onKeyDown}>
              ‹
            </button>
            <button className={css.carouselArrowNext} onClick={onSlideRight} onKeyDown={onKeyDown}>
              ›
            </button>
          </div>
          <div className={getColumnCSS(numColumns)} id={sliderId}>
            <BlockBuilder
              rootClassName={css.block}
              ctaButtonClass={css.verClosetLink}
              blocks={blocks}
              sectionId={sectionId}
              responsiveImageSizes={getResponsiveImageSizes(numColumns)}
              options={blockOptions}
            />
          </div>
        </div>
      ) : null}
    </SectionContainer>
  );
};

export default SectionUsersCarousel;
